/**
 * Velora public booking page: `/b/<office-slug>`. Three screens (Visit,
 * Time, Details) and a confirmation, on the same engine as the staff
 * preview, DentChat and the voice receptionist.
 */
import type { PublicSite } from '@velora/contracts/booking';
import { Button, Skeleton } from '@velora/ui';
import { AlertTriangle, MapPin, Monitor, Moon, Phone, ShieldCheck, Sun } from 'lucide-react';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { flowReducer, initialFlow, STEPS, type FlowState } from './flow.js';
import { createBookingApi, isNotFound, messageOf, type BookingApi } from './lib/api.js';
import { parseEntry, withoutToken, type Entry } from './lib/entry.js';
import { fitsAgeBand, formatPhoneInput, telHref, whenLabel } from './lib/format.js';
import { useAsync } from './lib/hooks.js';
import {
  loadIdentity,
  loadTheme,
  saveIdentity,
  saveTheme,
  visitorId as makeVisitorId,
  type ThemeChoice,
} from './lib/storage.js';
import { Callback } from './screens/Callback.js';
import { DetailsStep, emptyDraft, type DetailsDraft } from './screens/DetailsStep.js';
import { DoneStep } from './screens/DoneStep.js';
import { Identify } from './screens/Identify.js';
import { policyPageFromPath, SmsPolicy, type PolicyPage } from './screens/SmsPolicy.js';
import { TimeStep } from './screens/TimeStep.js';
import { VisitStep } from './screens/VisitStep.js';

function readEntry(): Entry {
  return parseEntry(window.location.href, document.referrer);
}

function useTheme(): [ThemeChoice, (t: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(loadTheme);
  useEffect(() => {
    const apply = () => {
      const system =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: light)').matches
          ? 'light'
          : 'dark';
      document.documentElement.dataset['theme'] = choice === 'system' ? system : choice;
    };
    apply();
    if (choice !== 'system' || typeof window.matchMedia !== 'function') return;
    const m = window.matchMedia('(prefers-color-scheme: light)');
    m.addEventListener('change', apply);
    return () => m.removeEventListener('change', apply);
  }, [choice]);
  return [
    choice,
    (t) => {
      saveTheme(t);
      setChoice(t);
    },
  ];
}

export function App() {
  const [entry] = useState(readEntry);
  const [policyPage] = useState<PolicyPage | null>(() =>
    policyPageFromPath(window.location.pathname),
  );
  const slug = entry.slug;
  const [theme, setTheme] = useTheme();

  // A token link: keep the token for this tab only and take it out of the
  // address bar so it is not bookmarked, shared or sent as a referrer.
  const [identity, setIdentityState] = useState<string | null>(() => {
    if (!slug) return null;
    if (entry.token) {
      saveIdentity(slug, entry.token);
      try {
        window.history.replaceState(null, '', withoutToken(window.location.href));
      } catch {
        /* sandboxed */
      }
      return entry.token;
    }
    return loadIdentity(slug);
  });
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const setIdentity = useCallback(
    (t: string | null) => {
      if (slug) saveIdentity(slug, t);
      identityRef.current = t;
      setIdentityState(t);
    },
    [slug],
  );

  const api = useMemo(
    () => (slug ? createBookingApi(slug, () => identityRef.current) : null),
    [slug],
  );
  const siteKey = slug ? `${slug}|${identity ?? ''}` : null;
  // Keep the page mounted while identity changes refetch the site, so the
  // flow (and a finished booking) survives signing in or out.
  const site = useAsync<PublicSite>(siteKey, () => (api as BookingApi).site(), {
    keepPrevious: true,
  });
  const [linkExpired, setLinkExpired] = useState(false);

  // A token that no longer unlocks anything (used or expired): say so once.
  useEffect(() => {
    if (site.data && site.dataKey === siteKey && identity && site.data.identity === null) {
      setLinkExpired(true);
      setIdentity(null);
    }
  }, [site.data, site.dataKey, siteKey, identity, setIdentity]);

  useEffect(() => {
    if (!site.data) return;
    document.title = policyPage
      ? `${policyPage === 'sms-terms' ? 'Text message terms' : 'Privacy policy'} · ${site.data.practiceName}`
      : `Book a visit · ${site.data.officeName}`;
  }, [site.data, policyPage]);

  const phoneLabel = site.data?.phone ? formatPhoneInput(site.data.phone) : null;

  return (
    <div className="bw">
      <a className="bw-skip" href="#bw-main">
        Skip to booking
      </a>
      <header className="bw-top">
        <div className="bw-top__inner">
          <div className="bw-brand">
            <span className="bw-brand__mark" aria-hidden>
              {(site.data?.practiceName ?? 'V').replace(/[^A-Za-z]/g, '').slice(0, 1)}
            </span>
            <div className="bw-brand__text">
              {site.data ? (
                <>
                  <span className="bw-brand__name">{site.data.practiceName}</span>
                  <span className="bw-brand__office">{site.data.officeName}</span>
                </>
              ) : (
                <Skeleton width={180} height={14} />
              )}
            </div>
          </div>
          {site.data?.phone ? (
            <Button asChild variant="secondary" size="md" className="bw-top__call">
              <a href={telHref(site.data.phone)} aria-label={`Call ${phoneLabel ?? 'the office'}`}>
                <Phone size={16} aria-hidden />
                <span className="bw-top__phone">{phoneLabel}</span>
              </a>
            </Button>
          ) : null}
        </div>
      </header>

      <main id="bw-main" className="bw-main">
        {!slug || (site.error && isNotFound(site.error)) ? (
          <div className="bw-card bw-gone" data-testid="not-found">
            <AlertTriangle size={24} aria-hidden />
            <h1>This booking page is not available</h1>
            <p className="bw-dim">Check the link you were sent, or call the office to book.</p>
          </div>
        ) : site.error && !site.data ? (
          <div className="bw-card bw-gone" role="alert">
            <AlertTriangle size={24} aria-hidden />
            <h1>We could not load the booking page</h1>
            <p className="bw-dim">{messageOf(site.error)}</p>
            <Button variant="primary" size="lg" onClick={site.reload}>
              Try again
            </Button>
          </div>
        ) : !site.data || !api ? (
          <div className="bw-card bw-loading" aria-busy="true" aria-label="Loading">
            <Skeleton width="60%" height={28} />
            <Skeleton width="80%" height={14} />
            <Skeleton height={64} radius="lg" />
            <Skeleton height={64} radius="lg" />
            <Skeleton height={64} radius="lg" />
          </div>
        ) : policyPage ? (
          <SmsPolicy page={policyPage} site={site.data} />
        ) : (
          <FlowRoot
            key={site.data.slug}
            api={api}
            site={site.data}
            entry={entry}
            identity={identity}
            setIdentity={setIdentity}
            reloadSite={site.reload}
            linkExpired={linkExpired}
            markLinkExpired={() => setLinkExpired(true)}
            phoneLabel={phoneLabel}
          />
        )}
      </main>

      <footer className="bw-foot">
        <p className="bw-foot__secure">
          <ShieldCheck size={14} aria-hidden /> Your details go only to this dental office.
        </p>
        <div className="bw-theme" role="radiogroup" aria-label="Appearance">
          {(
            [
              ['system', <Monitor key="s" size={14} />, 'Match device'],
              ['light', <Sun key="l" size={14} />, 'Light'],
              ['dark', <Moon key="d" size={14} />, 'Dark'],
            ] as const
          ).map(([v, icon, label]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={theme === v}
              aria-label={label}
              title={label}
              onClick={() => setTheme(v)}
            >
              {icon}
            </button>
          ))}
        </div>
        <p className="bw-foot__powered">Online booking by Velora Dental</p>
      </footer>
    </div>
  );
}

interface FlowProps {
  api: BookingApi;
  site: PublicSite;
  entry: Entry;
  identity: string | null;
  setIdentity: (t: string | null) => void;
  reloadSite: () => void;
  linkExpired: boolean;
  markLinkExpired: () => void;
  phoneLabel: string | null;
}

const STEP_FOR_FUNNEL = { visit: 'type', time: 'when', details: 'details', done: 'done' } as const;

function FlowRoot(p: FlowProps) {
  const { site, api } = p;
  const identified = site.identity !== null && p.identity !== null;
  const visitorId = useMemo(() => makeVisitorId(site.slug), [site.slug]);
  const [state, dispatch] = useReducer(flowReducer, undefined, (): FlowState =>
    initialFlow({
      identified,
      typeId: p.entry.typeId,
      typeVisible: site.types.some(
        (t) =>
          t.id === p.entry.typeId &&
          fitsAgeBand(t.ageBand, site.identity?.birthdate ?? null, site.today),
      ),
    }),
  );
  const [draft, setDraft] = useState<DetailsDraft>(() => emptyDraft(null));
  const [showIdentify, setShowIdentify] = useState(false);
  const anonApi = useMemo(() => createBookingApi(site.slug, () => null), [site.slug]);
  const otherTypes = useAsync(
    identified && state.bookingFor !== 'self' ? `${site.slug}|anon` : null,
    () => anonApi.site().then((s) => s.types),
  );

  const allTypes = useMemo(
    () => [
      ...site.types,
      ...(otherTypes.data ?? []).filter((t) => !site.types.some((x) => x.id === t.id)),
    ],
    [site.types, otherTypes.data],
  );
  const type = allTypes.find((t) => t.id === state.typeId) ?? null;

  // Funnel progress (OB-21 by source, OB-19 abandonment groups by visitor).
  const lastStep = useRef<string>('');
  useEffect(() => {
    const step = STEP_FOR_FUNNEL[state.step];
    const sig = `${step}|${state.typeId ?? ''}|${state.hold?.start ?? ''}`;
    if (lastStep.current === sig || step === 'done') return;
    lastStep.current = sig;
    api
      .progress({
        visitorId,
        step,
        ...(state.typeId ? { typeId: state.typeId } : {}),
        ...(state.hold ? { slotStart: state.hold.start } : {}),
        transactionalConsent: false,
        attribution: p.entry.attribution,
      })
      .catch(() => undefined);
  }, [api, visitorId, state.step, state.typeId, state.hold, p.entry.attribution]);

  // Move focus to the new screen's top for keyboard and screen-reader users.
  const top = useRef<HTMLDivElement>(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    top.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [state.step]);

  if (site.identity?.mustCall) {
    return (
      <div className="bw-card" data-testid="must-call">
        <h1 className="bw-h1">Please call us to book</h1>
        <p className="bw-dim">
          {site.identity.firstName}, the office would like to book your next visit with you by
          phone.
        </p>
        <Callback
          api={api}
          visitorId={visitorId}
          practiceName={site.practiceName}
          officePhone={site.phone}
          officePhoneLabel={p.phoneLabel}
          kind="must_call"
          attribution={p.entry.attribution}
          bot={site.turnstile}
          title="Or ask us to call you"
          lead="Leave your number and the office will call you back."
          firstName={site.identity.firstName}
        />
      </div>
    );
  }

  // A token link that does not open a record on this site (expired, used,
  // or made for another office): never fall back to booking anonymously
  // under someone else's link (PRACTICE-ISOLATION-AUDIT M6).
  if (p.linkExpired) {
    return (
      <div className="bw-card bw-gone" data-testid="link-expired" role="alert">
        <AlertTriangle size={24} aria-hidden />
        <h1 className="bw-h1">This booking link has expired</h1>
        <p className="bw-dim">
          Please call {site.officeName}
          {p.phoneLabel ? ` at ${p.phoneLabel}` : ''} to book your visit.
        </p>
        {site.phone ? (
          <Button asChild variant="primary" size="lg">
            <a href={telHref(site.phone)}>
              <Phone size={16} aria-hidden /> Call the office
            </a>
          </Button>
        ) : null}
      </div>
    );
  }

  const stepIndex = state.step === 'done' ? 3 : STEPS.findIndex((s) => s.step === state.step);

  return (
    <div className="bw-layout">
      <div className="bw-card bw-flow">
        <div ref={top} tabIndex={-1} className="bw-flow__top">
          {state.step !== 'done' ? (
            <>
              <h1 className="bw-h1">{site.headline || 'Book a visit'}</h1>
              {site.intro && state.step === 'visit' ? (
                <p className="bw-intro">{site.intro}</p>
              ) : null}
              <ol className="bw-steps" aria-label="Progress">
                {STEPS.map((s, i) => (
                  <li
                    key={s.step}
                    data-state={i < stepIndex ? 'done' : i === stepIndex ? 'current' : 'todo'}
                  >
                    {i < stepIndex ? (
                      <button
                        type="button"
                        onClick={() => dispatch({ type: 'goto', step: s.step })}
                      >
                        <span className="bw-steps__n">{i + 1}</span>
                        {s.label}
                      </button>
                    ) : (
                      <span aria-current={i === stepIndex ? 'step' : undefined}>
                        <span className="bw-steps__n">{i + 1}</span>
                        {s.label}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </>
          ) : null}
        </div>

        {state.step === 'visit' ? (
          <VisitStep
            site={site}
            otherTypes={otherTypes.data}
            patientKind={state.patientKind}
            bookingFor={state.bookingFor}
            family={state.family}
            showIdentify={showIdentify || (state.patientKind === 'existing' && !identified)}
            identifyPanel={
              <Identify
                api={api}
                today={site.today}
                bot={site.turnstile}
                onVerified={(t) => {
                  p.setIdentity(t);
                  setShowIdentify(false);
                  dispatch({ type: 'identified' });
                }}
                onCancel={() => {
                  setShowIdentify(false);
                  dispatch({ type: 'patientKind', kind: 'new' });
                }}
              />
            }
            onPatientKind={(k) => {
              dispatch({ type: 'patientKind', kind: k });
              setShowIdentify(k === 'existing');
            }}
            onBookingFor={(v) => dispatch({ type: 'bookingFor', value: v })}
            onChooseType={(id) => dispatch({ type: 'chooseType', typeId: id })}
            onSignOut={() => {
              p.setIdentity(null);
              dispatch({ type: 'signedOut' });
            }}
          />
        ) : null}

        {state.step === 'time' && type ? (
          <TimeStep
            api={api}
            site={site}
            type={type}
            providerId={state.providerId}
            hold={state.hold}
            notice={state.notice}
            family={state.family}
            visitorId={visitorId}
            attribution={p.entry.attribution}
            phoneLabel={p.phoneLabel}
            onProvider={(id) => dispatch({ type: 'provider', providerId: id })}
            onHeld={(hold) => dispatch({ type: 'held', hold })}
            onChangeType={() => {
              if (state.hold)
                void api.release(state.hold.holdId, state.hold.holdToken).catch(() => undefined);
              dispatch({ type: 'back' });
            }}
          />
        ) : null}

        {state.step === 'details' && type && state.hold ? (
          <DetailsStep
            api={api}
            site={site}
            type={type}
            hold={state.hold}
            bookingFor={state.bookingFor}
            identityToken={identified ? p.identity : null}
            attribution={p.entry.attribution}
            visitorId={visitorId}
            familyOf={state.family?.reference ?? null}
            draft={draft}
            onDraft={setDraft}
            onHeld={(hold) => dispatch({ type: 'held', hold })}
            onHoldLost={(message) => dispatch({ type: 'holdLost', message })}
            onIdentityExpired={() => {
              p.markLinkExpired();
              p.setIdentity(null);
            }}
            onSubmitted={(result, first, booker) => {
              dispatch({ type: 'submitted', result, patientFirstName: first, booker });
              setDraft(emptyDraft(booker));
              // The identity token is single-use (OB-09).
              if (identified && state.bookingFor === 'self') p.setIdentity(null);
            }}
            onBack={() => dispatch({ type: 'back' })}
          />
        ) : null}

        {state.step === 'done' && state.result ? (
          <DoneStep
            site={site}
            result={state.result}
            booked={state.booked}
            phoneLabel={p.phoneLabel}
            onBookAnother={() => dispatch({ type: 'bookAnother' })}
          />
        ) : null}
      </div>

      <aside className="bw-aside" aria-label="Office and your visit">
        <div className="bw-card bw-office">
          <p className="bw-aside__title">{site.officeName}</p>
          {site.address.line1 ? (
            <p className="bw-office__line">
              <MapPin size={14} aria-hidden />
              <span>
                {site.address.line1}
                <br />
                {[site.address.city, site.address.state].filter(Boolean).join(', ')}{' '}
                {site.address.postalCode}
              </span>
            </p>
          ) : null}
          {site.phone ? (
            <p className="bw-office__line">
              <Phone size={14} aria-hidden />
              <a href={telHref(site.phone)}>{p.phoneLabel}</a>
            </p>
          ) : null}
        </div>
        {type && (state.step === 'time' || state.step === 'details') ? (
          <div className="bw-card bw-visit" data-testid="visit-summary">
            <p className="bw-aside__title">Your visit</p>
            <p>{type.label}</p>
            {state.hold ? (
              <p className="bw-dim">{whenLabel(state.hold.start, site.timezone, site.today)}</p>
            ) : (
              <p className="bw-dim">Choose a time</p>
            )}
          </div>
        ) : null}
      </aside>
    </div>
  );
}
